"use client";

import React, { useState } from "react";
import { useHubspotForm } from "@/features/forms/hooks/useHubspotForm";
import { Textfield } from "@/components/ui/form/Textfield";
import { FriendlyCaptchaWidget } from "@/components/forms/FriendlyCaptchaWidget";
import { Button } from "@/components/ui/buttons";
import type { ThemeVariant } from "@/types";
import type { FormDefinition } from "@/features/forms/types";
import { getTranslations, type Locale } from "@/lib/i18n/config";
import { pushToDataLayer } from "@/lib/analytics/pushToDataLayer";
import { hashEmail } from "@/lib/analytics/hashUserData";
import { useNewsletterTool } from "@/features/webmcp";
import { Heading } from "@/components/ui/Heading";

const FooterNewsletter = ({
  variant = "dark",
  formDefinition,
  locale = "de",
}: {
  variant?: ThemeVariant;
  formDefinition: FormDefinition;
  locale?: Locale;
}) => {
  const [email, setEmail] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  // Reveal the captcha once the user starts interacting; never hide it again.
  const [started, setStarted] = useState(false);

  const { newsletter: t } = getTranslations(locale).forms;

  const { submitForm, loading, success, error } = useHubspotForm({
    formDefinition,
  });

  // Expose the same submission as a WebMCP tool (reuses field/consent handling).
  useNewsletterTool(async (email) => {
    const res = await submitForm({ email });
    if (res.success) {
      pushToDataLayer({
        event: "newsletter_abo",
        user_data: { sha256_email_address: await hashEmail(email) },
      });
    }
    return res;
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setValidationError(t.validationError);
      return;
    }

    setValidationError(null);
    // The token is one-time: verification consumes it whatever the outcome,
    // so clear it now — a retry falls back to a fresh headless solve.
    const token = captchaToken ?? undefined;
    setCaptchaToken(null);
    await submitForm({ email }, token);
    pushToDataLayer({
      event: "newsletter_abo",
      user_data: { sha256_email_address: await hashEmail(email) },
    });
  };

  return (
    <>
      <Heading style="h3" className="mb-sm">
        {t.headline}
      </Heading>
      <p className="mb-md">{t.description}</p>

      {success ? (
        <div className="p-md border-2 border-yellow/40 rounded-2xl bg-yellow/10">
          <p
            className={`font-weight-medium ${variant === "dark" ? "text-black" : "text-off-white"}`}
          >
            {t.success}
          </p>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          onFocusCapture={() => setStarted(true)}
          noValidate
        >
          <div className="flex flex-col sm:flex-row gap-sm items-start">
            <div className="flex-1 w-full">
              <Textfield
                type="email"
                name="email"
                placeholder={t.placeholder}
                color={variant === "dark" ? "purple" : "dark"}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (validationError) setValidationError(null);
                }}
                disabled={loading}
                error={validationError ?? undefined}
                aria-label="E-Mail-Adresse für Newsletter-Anmeldung"
                autoComplete="email"
              />
            </div>

            <div className="shrink-0 mt-0.5">
              <Button
                type="submit"
                variant="primary"
                color={variant === "dark" ? "purple" : "yellow"}
                disabled={loading}
                icon="arrow-right"
                label={loading ? t.sending : t.submit}
              />
            </div>
          </div>
          <div
            className={`grid transition-[grid-template-rows,opacity] duration-500 ease-out ${
              started
                ? "grid-rows-[1fr] opacity-100 mt-sm"
                : "grid-rows-[0fr] opacity-0"
            }`}
          >
            <div className="overflow-hidden">
              {started && (
                <FriendlyCaptchaWidget
                  onVerify={setCaptchaToken}
                  theme={variant === "dark" ? "light" : "dark"}
                />
              )}
            </div>
          </div>

          <p className="text-xs opacity-60 mt-sm">{t.disclaimer}</p>

          {error && !validationError && (
            <p className="mt-sm text-sm text-red-400">{error}</p>
          )}
        </form>
      )}
    </>
  );
};

export { FooterNewsletter };
